import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import {
  ImagingStudy,
  RXDB_IMAGING_STUDY_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/imaging-study";
import type { Patient } from "$global-providers/rxdb-provider/collections/patient";
import { useUser } from "$global-providers/user-provider";
import type { PatientDetailsQuery } from "$routes/patient/[:patientId]/_lib/__generated__/patient-queries.gql";
import { RegistrationPaymentDialog } from "$routes/patient/[:patientId]/_lib/patient-info-dropdown/popover/registration-payment-dialog";
import { gql } from "@apollo/client";
import { Divider, styled } from "@material-ui/core";
import {
  type ContactPointOwnership,
  ContactPointSystem,
  ContactRelationship,
  type Gender,
  PaymentOrigin,
  type PaymentStatus,
  Role,
} from "elephant-web/gql";
import { useMemo, useState } from "react";
import { useRxCollection } from "rxdb-hooks";
import {
  type PatientCentreOnlineOnlyFieldsQuery,
  usePatientCentreOnlineOnlyFieldsQuery,
} from "./__generated__/index.gql";
import { BasicDetails } from "./basic-details";
import { GenerateImagingStudyCopyIdButton } from "./generate-imaging-study-copy-id-button";
import { QueueMenu } from "./queue-menu";
import { StatusRow } from "./status-row";
import { ThreeDotMenu } from "./three-dot-menu";

gql`
  query PatientCentreOnlineOnlyFields($id: String!) {
    patientByCardId(uuid: $id) {
      patient {
        uuid
        insurance {
          insuranceType
          insuranceNumber
          paymentUpToDate
          expiryDate
          visitValidation {
            expiryStrategy
          }
          insuranceProvider {
            name
            visitValidation {
              expiryStrategy
            }
          }
          insuranceDetails
        }
        currentVisit {
          ...PatientCurrentVisit
        }
        registrationPayment {
          status
        }
        card {
          id
        }
      }
      passportId
    }
  }
`;

// Ported over the type from the old patient info dropdown to avoid having to refactor all the downstream components.
// Ideally we'd do that and make this type more narrow, for example not relying on the `PatientDetailsQuery` type.
export type PatientCentrePatient = {
  uuid: string;
  cardId?: string;
  insurance?: PatientCentreOnlineOnlyFieldsQuery["patientByCardId"]["patient"]["insurance"];
  person: Omit<
    PatientDetailsQuery["patientByCardId"]["patient"]["person"],
    "maritalStatus"
  >;
  currentVisit?: {
    insuranceList?: PatientCentreOnlineOnlyFieldsQuery["patientByCardId"]["patient"]["currentVisit"]["insuranceList"];
  };
  registrationPayment?: { status: PaymentStatus };
};

const Container = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.palette.common.white,
  // Negative margins bleed the header to the page edges, cancelling the
  // surrounding page padding. The pull (and the inner padding that re-indents
  // the content) must match that padding per breakpoint — 16px on xs, 24px
  // sides from sm up — or it over-pulls and breaks the layout / misaligns.
  margin: `-${theme.spacing(2)}px -${theme.spacing(2)}px 0 -${theme.spacing(2)}px`,
  padding: `12px ${theme.spacing(2)}px`,
  [theme.breakpoints.up("sm")]: {
    margin: `-${theme.spacing(2)}px -${theme.spacing(3)}px ${theme.spacing(2.5)}px`,
    padding: `12px ${theme.spacing(3)}px`,
  },
}));

const FirstRow = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "12px",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    gap: "12px",
  },
}));

const ButtonsContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const SecondRow = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  marginTop: "12px",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    gap: "12px",
  },
}));

export const PatientCentre: React.FC<{
  patient: Patient;
}> = ({ patient: rxPatient }) => {
  const [patientPayFlag] = useFeatureFlag(featureFlags.patientPay);
  const imagingStudyCollection = useRxCollection<ImagingStudy>(
    RXDB_IMAGING_STUDY_COLLECTION_NAME,
  );
  const user = useUser();

  const [isRenewalPaymentModalOpen, setIsRenewalPaymentModalOpen] =
    useState(false);
  // Still using the online patient data for insurance and other fields not available in rxdb
  const { data: gqlData } = usePatientCentreOnlineOnlyFieldsQuery({
    variables: { id: rxPatient.id },
  });
  const patient = useMemo(() => {
    const mappedPatient = mapRxPatientToPatientCentre(rxPatient);
    const onlinePatient = gqlData?.patientByCardId?.patient;
    if (onlinePatient) {
      mappedPatient.registrationPayment = onlinePatient.registrationPayment;
      mappedPatient.insurance = onlinePatient.insurance;
      mappedPatient.currentVisit = onlinePatient.currentVisit;
      mappedPatient.cardId = onlinePatient.card?.id;
    }
    return mappedPatient;
  }, [gqlData, rxPatient]);

  return (
    <>
      <Container>
        <FirstRow>
          <BasicDetails
            name={constructFullName(patient.person)}
            dob={new Date(patient.person.dob)}
            sex={patient.person.gender}
          />
          <ButtonsContainer>
            {user.role === Role.Sonographer ? (
              <GenerateImagingStudyCopyIdButton
                patientId={patient.uuid}
                userId={user.id}
                imagingStudyCollection={imagingStudyCollection}
              />
            ) : (
              <QueueMenu patient={patient} />
            )}
            <ThreeDotMenu patient={patient} />
          </ButtonsContainer>
        </FirstRow>
        <Divider />
        <SecondRow>
          <StatusRow
            patient={patient}
            isPatientPayEnabled={patientPayFlag}
            onClickLogRenewalPayment={
              user.role !== Role.Sonographer
                ? () => setIsRenewalPaymentModalOpen(true)
                : undefined
            }
          />
        </SecondRow>
      </Container>
      <RegistrationPaymentDialog
        open={isRenewalPaymentModalOpen}
        onClose={() => setIsRenewalPaymentModalOpen(false)}
        patientId={patient.uuid}
        origin={PaymentOrigin.Renewal}
      />
    </>
  );
};

const mapRxPatientToPatientCentre = (
  patient: Patient,
): PatientCentrePatient => ({
  uuid: patient.id,
  person: {
    name: {
      firstname: patient.name.first,
      middlename: patient.name.middle,
      lastname: patient.name.last,
    },
    gender: patient.gender as Gender,
    languages: [], // not in rxdb?
    dob: patient.dob,
    addresses: [
      {
        state: patient.address.line1,
        subCounty: patient.address.line2,
        ward: patient.address.line3,
        street: patient.address.line4,
        country: patient.address.country,
      },
    ],
    nationality: patient.nationality,
    contacts: patient.nextOfKin
      ? [
          {
            name: {
              fullname: patient.nextOfKin.fullName,
            },
            contactPoints: [
              {
                system: ContactPointSystem.Phone,
                value: patient.nextOfKin.phoneNumber,
              },
            ],
            relationship: ContactRelationship.N,
          },
        ]
      : [],
    contactPoints: patient.phone
      ? [
          {
            system: ContactPointSystem.Phone,
            ownership: patient.phone.ownership as ContactPointOwnership,
            value: patient.phone.number,
          },
        ]
      : [],
  },
});

export const constructFullName = (
  person: PatientDetailsQuery["patientByCardId"]["patient"]["person"],
) => {
  return `${person.name.firstname}${
    person.name.middlename ? ` ${person.name.middlename}` : ""
  } ${person.name.lastname}`;
};
